import { useEffect, useRef, useState } from "react";
import {
  addFavoriteSession,
  hideSession,
  removeFavorite,
  renameFavoriteBySignature,
  saveSessionFeedback,
  saveSessionStart,
  unhideSession,
} from "../../services/ai/memory/aiMemory.js";
import { auth } from "../../firebase-config.js";
import MoodSlider from "./MoodSlider";
import { EyeOff, Heart } from "lucide-react";
import "./AISessionFeedback.css";
import {
  clearCustomTitleForSession,
  getTitleForSession,
  setCustomTitleForSession,
} from "../../services/ai/composer/narrative.js";


function AISessionFeedback({
  session,
  stateBefore,
  intent,
  listenedSeconds,
  onSaveNote,
  onDone,
}) {
const [stateAfter, setStateAfter] = useState(() => {
  const initialValue = Number(stateBefore);
  return Number.isFinite(initialValue) ? initialValue : 5;
});
const [stateTouched, setStateTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [ackMessage, setAckMessage] = useState(null);
  const [action, setAction] = useState(null);
  const [toast, setToast] = useState(null);
  const [customTitle, setCustomTitle] = useState("");
  const [journalOpen, setJournalOpen] =
  useState(false);

const [journalNote, setJournalNote] =
  useState("");

const [journalError, setJournalError] =
  useState("");

const journalSavedRef = useRef(false);
const savingRef = useRef(false);
const [saveError, setSaveError] = useState("");
  const [favoriteTitle, setFavoriteTitle] = useState("");
  const toastTimerRef = useRef(null);
  const ackTimerRef = useRef(null);

  const sessionIntent = intent || session?.context?.intent || "relax";
  const actionWeight = action === "favorite" ? 2 : action === "hide" ? -2 : 0;
  const delta = Number.isFinite(Number(stateBefore))
    ? stateAfter - Number(stateBefore)
    : null;

  useEffect(
    () => () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      if (ackTimerRef.current) window.clearTimeout(ackTimerRef.current);
    },
    [],
  );

  const showToast = (message, undoFn = null) => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToast({ message, undoFn });
    toastTimerRef.current = window.setTimeout(() => setToast(null), undoFn ? 7000 : 2600);
  };

const handleFavorite = async () => {
  const uid = auth.currentUser?.uid;

  if (
    !uid ||
    !session ||
    action === "favorite"
  ) {
    return;
  }

  const generatedTitle =
    getTitleForSession(session);

  const sessionForFavorite = {
    ...session,
    title: generatedTitle,
  };

  setFavoriteTitle(generatedTitle);
  setAction("favorite");

  try {
    const result = await addFavoriteSession(
      uid,
      sessionForFavorite,
    );

    const signature = (
      session.arc?.tracks || []
    )
      .map((track) => track.id)
      .join("|");

    const favorite =
      result?.favorites?.find(
        (item) =>
          item.signature === signature,
      );

    showToast(
      "Ajouté à vos refuges. Retrouvez-le dans votre profil.",
      async () => {
        if (favorite) {
          await removeFavorite(
            uid,
            favorite.favoriteId,
          );
        }

        clearCustomTitleForSession(session);
        setFavoriteTitle("");
        setCustomTitle("");
        setAction(null);
        setToast(null);
      },
    );
  } catch (error) {
    console.warn(
      "Impossible d’ajouter le favori :",
      error,
    );

    clearCustomTitleForSession(session);
    setFavoriteTitle("");
    setAction(null);

    showToast(
      "Impossible d’ajouter cette composition aux favoris.",
    );
  }
};

  const handleHide = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid || !session || action === "hide") return;
    setAction("hide");
    const result = await hideSession(uid, session);
    const signature = (session.arc?.tracks || []).map((track) => track.id).join("|");
    const hidden = result?.hiddenSessions?.find((item) => item.signature === signature);

    showToast("Cette composition ne vous sera plus proposée", async () => {
      if (hidden) await unhideSession(uid, hidden.hiddenId);
      setAction(null);
      setToast(null);
    });
  };

  const buildAcknowledgement = () => {
    if (action === "favorite") return "C'est noté. Vous retrouverez cette session dans votre profil.";
    if (action === "hide") return "C'est compris. Cette composition ne reviendra plus.";
    if (!stateTouched) return "Ce moment est terminé. Prenez encore un instant avant de reprendre votre journée.";
    if (delta !== null && delta < 0) return "C’est entendu. La prochaine fois, une autre proposition vous attendra.";
    if (delta !== null && delta >= 2) return "Ce moment vous a fait du bien. Merci pour votre retour.";
    return "Votre ressenti est enregistré. Les prochaines propositions en tiendront compte.";
  };

  const finalAmbiances = (session?.ambiances || []).map((item) => ({
    id: item.id,
    volume: Number(item.volume) || 0,
  }));

const persistCompletion = async (feedbackSkipped) => {
  const uid = auth.currentUser?.uid;
  if (!uid || !session?.id) throw new Error("Votre compte n’est plus disponible.");
  await saveSessionStart(uid, session, stateBefore);
  await saveSessionFeedback(uid, session.id, { stateAfter: stateTouched ? stateAfter : null, listenedSeconds, feedbackWeight: actionWeight, feedbackSkipped, finalAmbiances });
  if (action === "favorite" && customTitle.trim()) {
    setCustomTitleForSession(session, customTitle);
    await renameFavoriteBySignature(uid, session, customTitle);
  }
};

const saveJournal = async () => {
  const cleanJournalNote = journalNote.trim();
  if (!cleanJournalNote || typeof onSaveNote !== "function" || journalSavedRef.current) return;
  await onSaveNote(cleanJournalNote);
  journalSavedRef.current = true;
};

const handleSubmit = async () => {
  if (savingRef.current) return;
  savingRef.current = true;
  setIsSaving(true);
  setSaveError("");
  setJournalError("");
  try {
    await persistCompletion(!stateTouched && actionWeight === 0);
    try { await saveJournal(); } catch { setJournalError("Votre ressenti est enregistré. La note n’a pas pu être ajoutée à votre journal."); return; }
    setAckMessage(buildAcknowledgement());
    ackTimerRef.current = window.setTimeout(() => onDone?.(), 2400);
  } catch (error) {
    console.warn("Enregistrement du ressenti impossible :", error);
    setSaveError("Votre ressenti n’a pas été enregistré. Vérifiez votre connexion puis réessayez.");
  } finally {
    savingRef.current = false;
    setIsSaving(false);
  }
};

const handleSkip = async () => {
  if (savingRef.current) return;
  savingRef.current = true;
  setIsSaving(true);
  setSaveError("");
  try {
    await persistCompletion(actionWeight === 0);
    try { await saveJournal(); } catch (error) { console.warn("Erreur sauvegarde journal Auria :", error); }
    onDone?.();
  } catch (error) {
    console.warn("Enregistrement de fin de séance impossible :", error);
    setSaveError("La fin de séance n’a pas été enregistrée. Vérifiez votre connexion puis réessayez.");
  } finally {
    savingRef.current = false;
    setIsSaving(false);
  }
};

  const formatDuration = (seconds) => {
    const value = Math.max(0, Math.round(Number(seconds) || 0));
    const minutes = Math.floor(value / 60);
    const rest = value % 60;
    if (!minutes) return `${rest} sec`;
    return rest ? `${minutes} min ${rest}` : `${minutes} min`;
  };

  if (ackMessage) {
    return (
      <div className="aif-overlay">
        <div className="aif-container aif-ack">
          <p className="aif-ack__text">{ackMessage}</p>
          <div className="aif-ack__logo-wrap" aria-hidden="true">
            <img
              src="/refuge-symbol.svg"
              alt=""
              className="aif-ack__logo"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aif-overlay">
      <div className="aif-container">
        <div className="aif-header">
          <div className="aif-pill">Moment terminé · {formatDuration(listenedSeconds)}</div>
          <h2 className="aif-title">Et maintenant&nbsp;?</h2>
          <p className="aif-subtitle">Prenez le temps de le remarquer.</p>
        </div>

<div className="aif-section aif-section--mood">
  <div className="aif-section-label">Votre ressenti</div>
          <MoodSlider
            intent={sessionIntent}
            value={stateAfter}
            onChange={(value) => { setStateTouched(true); setStateAfter(value); }}
            variant="after"
          />

          {stateTouched && delta !== null && (
            <div
              className={`aif-delta ${
                delta > 0
                  ? "aif-delta--up"
                  : delta < 0
                    ? "aif-delta--down"
                    : "aif-delta--flat"
              }`}
            >
              <span className="aif-delta-arrow">
                {delta > 0 ? "↑" : delta < 0 ? "↓" : "·"}
              </span>
              <span className="aif-delta-value">
                {delta > 0 ? "+" : ""}{delta} pts
              </span>
              <span className="aif-delta-label">depuis le début</span>
            </div>
          )}
        </div>

<div className="aif-section aif-section--composition">
  <div className="aif-section-label">Cette composition</div>
          <div className="aif-compo-actions">
            <button
              type="button"
              className="aif-compo-btn aif-compo-btn--love"
              data-active={action === "favorite"}
              aria-pressed={action === "favorite"}
              disabled={action === "hide"}
              onClick={handleFavorite}
            >
              <span className="aif-compo-icon" aria-hidden="true"><Heart size={17} strokeWidth={2.1} /></span>
              <span>Je l'ai aimée</span>
            </button>
            <button
              type="button"
              className="aif-compo-btn aif-compo-btn--hide"
              data-active={action === "hide"}
              aria-pressed={action === "hide"}
              disabled={action === "favorite"}
              onClick={handleHide}
            >
              <span className="aif-compo-icon" aria-hidden="true"><EyeOff size={17} strokeWidth={2.1} /></span>
              <span>Ne plus proposer</span>
            </button>
          </div>
{toast && (
  <div className="aif-toast" role="status">
    <span className="aif-toast-msg">{toast.message}</span>

    {toast.undoFn && (
      <button
        type="button"
        className="aif-toast-undo"
        onClick={toast.undoFn}
      >
        Annuler
      </button>
    )}
  </div>
)}
          {action === "favorite" && (
            <div className="aif-rename">
              <label className="aif-rename-label" htmlFor="auria-favorite-title">
                Donner un nom à ce refuge <span className="aif-optional">optionnel</span>
              </label>
              <input
                id="auria-favorite-title"
                type="text"
                className="aif-rename-input"
                maxLength={40}
                placeholder={favoriteTitle || "Votre refuge"}
                value={customTitle}
                onChange={(event) => setCustomTitle(event.target.value)}
              />
            </div>
          )}
        </div>
<div
  className={`aif-journal ${
    journalOpen ? "is-open" : ""
  }`}
>
  <button
    type="button"
    className="aif-journal__toggle"
    aria-expanded={journalOpen}
    onClick={() => {
      setJournalOpen(
        (current) => !current,
      );
      setJournalError("");
    }}
  >
    <span>
      {journalOpen
        ? "Refermer le journal"
        : "Ajouter quelques mots à mon journal"}
    </span>

    <span className="aif-optional">
      optionnel
    </span>
  </button>

  {journalOpen && (
    <div className="aif-journal__content">
      <label htmlFor="auria-journal-note">
        Comment vous sentez-vous après ce
        moment&nbsp;?
      </label>

      <textarea
        id="auria-journal-note"
        rows={3}
        maxLength={500}
        value={journalNote}
        placeholder="Une sensation, une pensée, quelques mots…"
        onChange={(event) => {
          setJournalNote(
            event.target.value,
          );
          setJournalError("");
        }}
      />

      <span className="aif-journal__count">
        {journalNote.length}/500
      </span>

      {journalError && (
        <p
          className="aif-journal__error"
          role="alert"
        >
          {journalError}
        </p>
      )}
    </div>
  )}
</div>
{saveError && <div><p className="aif-journal__error" role="alert">{saveError}</p><button type="button" className="aif-skip" onClick={() => onDone?.()}>Quitter sans enregistrer</button></div>}
<div className="aif-footer-actions">
  <button
    type="button"
    className="aif-cta"
    onClick={handleSubmit}
    disabled={isSaving}
  >
    {isSaving ? "Enregistrement…" : "Valider"}
  </button>

  <button
    type="button"
    className="aif-skip"
    onClick={handleSkip}
    disabled={isSaving}
  >
    Passer
  </button>
</div>
      </div>
    </div>
  );
}

export default AISessionFeedback;
